import { useState } from "react";
import styled from "styled-components";
import { Chip, Box } from "@material-ui/core";
import { CheckCircle, Cancel } from "@material-ui/icons";
import { Colors } from "theme";
import { PreviewContainer, Title, TitleRow, TopRow } from "routes/createContent/components/styles";
import Counter from "routes/createContent/components/Counter";
import parseHTML from "utils/parseHtml";

type ContentItem = {
  type: string;
  content: string;
};

type Question = {
  title: string;
  tip_description: string;
  answers: { text: string; correct: boolean }[];
};

interface LessonTranslation {
  title: string;
  content: ContentItem[];
  questions?: Question[];
}

interface LessonPart {
  order: number;
  translation: LessonTranslation;
}

export interface Lesson {
  name: string;
  target_joints?: string[];
  parts: LessonPart[];
}

interface Props {
  lesson: Lesson;
}

export const LessonPreview: React.VFC<Props> = ({ lesson }) => {
  const [currentPart, setCurrentPart] = useState(1);
  const selectedPart = lesson?.parts[currentPart - 1];

  const { order, translation } = selectedPart;
  const { content, questions = [] } = translation;

  const hasMoreThanOnePart = lesson?.parts.length > 1;

  return (
    <div>
      <h1>Lesson</h1>

      {lesson.target_joints && lesson.target_joints.length > 0 && (
        <TargetJointsRow>
          <TargetJointsLabel>Target joints:</TargetJointsLabel>
          {lesson.target_joints.map((joint) => (
            <Chip key={joint} label={joint.replace(/_/g, " ")} size="small" variant="outlined" />
          ))}
        </TargetJointsRow>
      )}

      <PreviewContainer>
        {hasMoreThanOnePart && (
          <TopRow>
            <Counter
              value={currentPart}
              maxValue={lesson?.parts.length ?? 1}
              minValue={1}
              clickCallback={newPart => setCurrentPart(newPart)}
            />
          </TopRow>
        )}

        <TitleRow>
          <Title>
            {translation.title} {order}
          </Title>
        </TitleRow>

        {content &&
          content.map(({ type, content }, index: number) => {
            const key = `${lesson.name}-${order}-${index}`;
            switch (type) {
              case "heading":
                return <Header key={key}>{content}</Header>;
              case "paragraph":
                return <Body key={key} dangerouslySetInnerHTML={{ __html: parseHTML(content) }} />;
              case "image":
                return <Image key={key} src={content} alt={translation.title} />;
              default:
                return null;
            }
          })}

        {questions.length > 0 && (
          <QuestionsSection>
            <QuestionsSectionTitle>Questions</QuestionsSectionTitle>
            {questions.map((question, qIndex) => (
              <QuestionCard key={`${lesson.name}-${order}-q-${qIndex}`}>
                <QuestionTitle>{question.title}</QuestionTitle>
                <AnswersList>
                  {question.answers.map((answer, aIndex) => (
                    <AnswerRow key={aIndex} $correct={answer.correct}>
                      {answer.correct ? (
                        <CheckCircle style={{ color: Colors.SUCCESS, fontSize: 20 }} />
                      ) : (
                        <Cancel style={{ color: Colors.DISABLED, fontSize: 20 }} />
                      )}
                      <AnswerText>{answer.text}</AnswerText>
                    </AnswerRow>
                  ))}
                </AnswersList>
                {question.tip_description && (
                  <TipBox>
                    <TipLabel>Tip</TipLabel>
                    {question.tip_description}
                  </TipBox>
                )}
              </QuestionCard>
            ))}
          </QuestionsSection>
        )}
      </PreviewContainer>
    </div>
  );
};

const TargetJointsRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TargetJointsLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.base};
`;

const Header = styled.h3`
  ${props => props.theme.font.header3};
  color: ${props => props.theme.colors.base};
  margin-bottom: 10px;
`;

const Body = styled.span`
  ${props => props.theme.font.body1}
  color: ${props => props.theme.colors.base};
  white-space: pre-wrap;
`;

const Image = styled.img`
  width: 100%;
`;

const QuestionsSection = styled.div`
  margin-top: 32px;
  border-top: 1px solid ${props => props.theme.colors.silver};
  padding-top: 24px;
`;

const QuestionsSectionTitle = styled.h3`
  ${props => props.theme.font.header3};
  color: ${props => props.theme.colors.base};
  margin: 0 0 16px;
`;

const QuestionCard = styled.div`
  border: 1px solid ${props => props.theme.colors.silver};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const QuestionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.base};
  margin: 0 0 12px;
`;

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AnswerRow = styled.div<{ $correct: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ $correct }) => ($correct ? "#e8f5e9" : "transparent")};
`;

const AnswerText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.theme.colors.base};
`;

const TipBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: ${props => props.theme.colors.redesign.db10};
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
`;

const TipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 13px;
  color: ${props => props.theme.palette.text.secondary};
`;
